import React, { useCallback, useRef, useState } from "react";
import { makeStyles, Tooltip } from "@material-ui/core";
import { MinusIcon } from "@radix-ui/react-icons";
import { BrandKitFontPreset } from "graphQL/mutations/brandKit";
import useTooltipStyles from "pages/flyer/builder/drawer/components/style";

interface BrandFontPresetRowProps {
  preset: BrandKitFontPreset;
  onApply: (preset: BrandKitFontPreset) => void;
  onEdit: (preset: BrandKitFontPreset, anchorEl: HTMLElement) => void;
  onRemove: (id: string) => void;
  onUpdateLabel: (id: string, label: string) => void;
}

const BrandFontPresetRow: React.FC<BrandFontPresetRowProps> = ({
  preset,
  onApply,
  onEdit,
  onRemove,
  onUpdateLabel,
}) => {
  const classes = useStyles();
  const classesTooltip = useTooltipStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(preset.label);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDoubleClick = useCallback(() => {
    setEditLabel(preset.label);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }, [preset.label]);

  const handleLabelSave = useCallback(() => {
    setIsEditing(false);
    const trimmed = editLabel.trim();
    if (trimmed && trimmed !== preset.label) {
      onUpdateLabel(preset.id, trimmed);
    } else {
      setEditLabel(preset.label);
    }
  }, [editLabel, preset.id, preset.label, onUpdateLabel]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleLabelSave();
      } else if (e.key === "Escape") {
        setIsEditing(false);
        setEditLabel(preset.label);
      }
    },
    [handleLabelSave, preset.label]
  );

  return (
    <div className={classes.row}>
      {/* Main bordered container */}
      <div className={classes.container} onClick={(e) => !isEditing && onEdit(preset, e.currentTarget as HTMLElement)}>
        {/* Left side: font label */}
        <div className={classes.leftSide}>
          <Tooltip
            title={preset.label}
            arrow
            placement="top"
            classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
          >
            <span className={classes.labelText}>{preset.label}</span>
          </Tooltip>
        </div>

        {/* Separator + lineHeight/letterSpacing — hidden for now, uncomment to re-enable
        <div className={classes.separator} />
        <div className={classes.rightSide}>
          <span className={classes.valuesText}>
            {(() => {
              const lineHeightPx =
                preset.fontSize > 0 ? Math.round(preset.lineHeight * preset.fontSize * 10) / 10 : preset.lineHeight;
              return `${lineHeightPx} / ${preset.letterSpacing}`;
            })()}
          </span>
        </div>
        */}
      </div>

      {/* Edit button removed — clicking the card container itself already
          opens the edit modal (see the onClick on `classes.container`
          above), so the dedicated icon was redundant. */}

      {/* Remove button */}
      <MinusIcon
        className={classes.actionIcon}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(preset.id);
        }}
      />
    </div>
  );
};

const useStyles = makeStyles({
  row: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  container: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    padding: "4px 8px",
    borderRadius: 4,
    border: "1px solid #E2E2FC",
    cursor: "pointer",
    position: "relative" as any,
    "&:hover": { borderColor: "#B0B0E8" },
  },
  leftSide: {
    width: "70%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  labelText: {
    fontSize: 12,
    color: "#2E3860",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as any,
  },
  labelInput: {
    fontSize: 12,
    color: "#2E3860",
    border: "1px solid #B0B0E8",
    borderRadius: 3,
    padding: "1px 4px",
    outline: "none",
    width: "100%",
    background: "white",
  },
  separator: {
    position: "absolute" as any,
    left: "70%",
    width: 1,
    height: 16,
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "#E2E2FC",
  },
  rightSide: {
    width: "30%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: 4,
  },
  valuesText: {
    fontSize: 10,
    color: "#2E3860",
    whiteSpace: "nowrap" as any,
  },
  actionIcon: {
    width: 16,
    height: 16,
    cursor: "pointer",
    color: "#2E3860",
    flexShrink: 0,
  },
});

export default BrandFontPresetRow;
